// Automatic latency measurement from the burn-in barcode (shared/src/burnin.ts).
//
// For each displayed frame (requestVideoFrameCallback gives the exact display
// time), sample the barcode strip, decode the server timestamp drawn just
// before encoding, and compare with this device's server-synchronised clock:
//   latency = display time (server clock) - burn-in time
// covering encode + network + jitter buffer + decode + render.

import { BURNIN_TOTAL_BLOCKS, burninGeometry, decodeBurnin, unwrapBurnin } from '../../../shared/src/burnin';

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { expectedDisplayTime: number; width: number; height: number }) => void) => number;
  cancelVideoFrameCallback?: (h: number) => void;
};

const CELL = 4;

export class LatencyProbe {
  private canvas = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private handle: number | null = null;
  private raf: number | null = null;
  private samples: number[] = [];
  private lastSample = 0;
  private misses = 0;
  /** Median of recent measurements, null when no burn-in is present. */
  latencyMs: number | null = null;

  constructor(
    private readonly video: FrameCallbackVideo,
    private readonly serverNow: (clientEpochMs: number) => number,
    private readonly onUpdate: (ms: number | null) => void,
  ) {
    this.canvas.width = BURNIN_TOTAL_BLOCKS * CELL;
    this.canvas.height = CELL;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
  }

  start(): void {
    this.stop();
    if (this.video.requestVideoFrameCallback) {
      const loop = (_now: number, meta: { expectedDisplayTime: number }) => {
        this.sample(performance.timeOrigin + meta.expectedDisplayTime);
        this.handle = this.video.requestVideoFrameCallback!(loop);
      };
      this.handle = this.video.requestVideoFrameCallback(loop);
    } else {
      // Fallback (older Firefox): sample on animation frames; slightly pessimistic.
      const loop = () => {
        this.sample(Date.now());
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    }
  }

  stop(): void {
    if (this.handle !== null) this.video.cancelVideoFrameCallback?.(this.handle);
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.handle = this.raf = null;
  }

  private sample(displayEpochMs: number): void {
    // ~6 measurements per second is plenty and keeps CPU use negligible on phones.
    if (displayEpochMs - this.lastSample < 160) return;
    this.lastSample = displayEpochMs;
    const v = this.video;
    if (!v.videoWidth || !v.videoHeight) return;
    const g = burninGeometry(v.videoWidth, v.videoHeight);
    try {
      this.ctx.drawImage(v, g.x0, g.y0, g.block * BURNIN_TOTAL_BLOCKS, g.block, 0, 0, this.canvas.width, this.canvas.height);
    } catch {
      return;
    }
    const px = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
    const luma: number[] = [];
    const y = CELL >> 1;
    for (let i = 0; i < BURNIN_TOTAL_BLOCKS; i++) {
      const x = i * CELL + (CELL >> 1);
      const o = (y * this.canvas.width + x) * 4;
      luma.push(0.2126 * px[o] + 0.7152 * px[o + 1] + 0.0722 * px[o + 2]);
    }
    const value = decodeBurnin(luma);
    if (value === null) {
      if (++this.misses > 20 && this.latencyMs !== null) {
        this.latencyMs = null;
        this.samples = [];
        this.onUpdate(null);
      }
      return;
    }
    this.misses = 0;
    const displayServer = this.serverNow(displayEpochMs);
    const burn = unwrapBurnin(value, displayServer);
    const lat = displayServer - burn;
    if (lat < -50 || lat > 10_000) return; // clock not synced yet, or garbage
    this.samples.push(lat);
    if (this.samples.length > 30) this.samples.shift();
    const sorted = [...this.samples].sort((a, b) => a - b);
    this.latencyMs = Math.round(sorted[sorted.length >> 1]);
    this.onUpdate(this.latencyMs);
  }
}

/** NTP-style offset estimate: keep the sample with the lowest round trip. */
export class ClockSync {
  offsetMs = 0;
  bestRtt = Infinity;
  synced = false;

  add(t0: number, server: number, t1: number): void {
    const rtt = t1 - t0;
    if (rtt > this.bestRtt * 1.5 && this.synced) return;
    if (rtt <= this.bestRtt) this.bestRtt = rtt;
    this.offsetMs = server - (t0 + rtt / 2);
    this.synced = true;
  }

  serverNow(clientEpochMs = Date.now()): number {
    return clientEpochMs + this.offsetMs;
  }
}
